class PriceSavings extends HTMLElement {
  connectedCallback() {
    const list = this.closest('price-list');
    if (!list) return;
    this._sale = list.querySelector('sale-price');
    this._compare = list.querySelector('compare-at-price');
    if (!this._sale || !this._compare) return;
    this._observer = new MutationObserver(() => this._update());
    const obsOpts = { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden'] };
    this._observer.observe(this._sale, obsOpts);
    this._observer.observe(this._compare, obsOpts);
    this._update();
  }
  disconnectedCallback() { this._observer?.disconnect(); }
  _update() {
    if (this._compare.hasAttribute('hidden')) { this.toggleAttribute('hidden', true); return; }
    const saleText = this._visibleText(this._sale);
    const compareText = this._visibleText(this._compare);
    const sale = this._toNumber(saleText);
    const compare = this._toNumber(compareText);
    if (!isFinite(sale) || !isFinite(compare) || compare <= sale) { this.toggleAttribute('hidden', true); return; }
    const saveLabel = this.dataset.saveLabel || 'SAVE';
    const format = this.dataset.saveFormat || 'percent';
    if (format === 'amount') {
      const savings = compare - sale;
      this.textContent = `${saveLabel} ${this._formatLike(savings, compareText)}`;
    } else {
      const percent = Math.round(((compare - sale) / compare) * 100);
      this.textContent = `${saveLabel} ${percent}%`;
    }
    this.toggleAttribute('hidden', false);
  }
  _visibleText(el) {
    return Array.from(el.childNodes)
      .filter((n) => !(n.nodeType === Node.ELEMENT_NODE && n.classList && n.classList.contains('sr-only')))
      .map((n) => n.textContent || '').join('').trim();
  }
  _toNumber(str) {
    /* Tabby (29 Oct 2026): prices are now shown in each language's format (1 234,50 € / €1,234.50 / 1.234 €), so read
       any of them: two kinds of separator -> the last is the decimal; one kind -> decimal only if it appears once with
       1-2 digits after it, otherwise it separates thousands */
    if (window.__tbPriceFormat && window.__tbPriceFormat.parse) return window.__tbPriceFormat.parse(str);
    let c = (str || '').replace(/[^\d.,-]/g, '');
    if (!c) return NaN;
    const d = c.lastIndexOf('.'), k = c.lastIndexOf(',');
    if (d > -1 && k > -1) c = d > k ? c.replace(/,/g, '') : c.replace(/\./g, '').replace(',', '.');
    else if (d > -1 || k > -1) {
      const ch = d > -1 ? '.' : ',', at = Math.max(d, k);
      c = (c.indexOf(ch) === at && c.length - at - 1 <= 2) ? c.replace(ch, '.') : c.split(ch).join('');
    }
    return parseFloat(c);
  }
  _formatLike(amount, referenceText) {
    if (window.__tbPriceFormat && window.__tbPriceFormat.format) {
      const f = window.__tbPriceFormat.format(amount, /[.,]\d{1,2}\D*$/.test(referenceText));
      if (f) return f;
    }
    const m = referenceText.match(/^(\D*)([\d.,]+)(\D*)$/);
    if (!m) return amount.toFixed(2);
    const [, prefix, numStr, suffix] = m;
    const lastDot = numStr.lastIndexOf('.');
    const lastComma = numStr.lastIndexOf(',');
    const useCommaDecimal = lastComma > lastDot;
    const decimalRaw = useCommaDecimal
      ? (lastComma >= 0 ? numStr.slice(lastComma + 1) : '')
      : (lastDot >= 0 ? numStr.slice(lastDot + 1) : '');
    const decimals = decimalRaw.length >= 1 && decimalRaw.length <= 4 ? decimalRaw.length : 0;
    let body = decimals ? amount.toFixed(decimals) : Math.round(amount).toString();
    const intPart = numStr.split(useCommaDecimal ? ',' : '.')[0];
    const groupChar = useCommaDecimal ? '.' : ',';
    const hasGrouping = intPart.includes(groupChar);
    let [whole, frac] = body.split('.');
    if (hasGrouping) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupChar);
    body = frac !== undefined ? whole + (useCommaDecimal ? ',' : '.') + frac : whole;
    return `${prefix}${body}${suffix}`;
  }
}
if (!customElements.get('price-savings')) customElements.define('price-savings', PriceSavings);
